import { clientEnv } from '@/shared/config/env';

/** 同一 origin 下 US / oro / Rosa 共用 localStorage；key 必须带 siteId，否则会串登录态。 */
function sessionScope() {
  return clientEnv.siteId || 'default';
}

function tokenKey() {
  return `fc.player.session.${sessionScope()}`;
}

function expiresKey() {
  return `fc.player.session.expiresAt.${sessionScope()}`;
}

function cookieName() {
  return `fc_player_session_${sessionScope()}`;
}

let memoryToken = '';
let memoryExpiresAt = '';

function persistEnabled() {
  return clientEnv.allowSessionStorage;
}

function readCookie(): { expiresAt: string; token: string } | null {
  if (typeof document === 'undefined') return null;
  const name = `${cookieName()}=`;
  const raw = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(name));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw.slice(name.length))) as { e?: unknown; t?: unknown };
    if (typeof parsed.t !== 'string' || !parsed.t) return null;
    return { expiresAt: typeof parsed.e === 'string' ? parsed.e : '', token: parsed.t };
  } catch {
    return null;
  }
}

function writeCookie(token: string, expiresAt: string) {
  if (typeof document === 'undefined' || !persistEnabled()) return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  if (!token) {
    document.cookie = `${cookieName()}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    return;
  }
  const value = encodeURIComponent(JSON.stringify({ e: expiresAt, t: token }));
  let maxAge = 60 * 60 * 24 * 365;
  const ts = Date.parse(expiresAt);
  if (Number.isFinite(ts)) maxAge = Math.max(0, Math.floor((ts - Date.now()) / 1000));
  document.cookie = `${cookieName()}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function adoptCookie() {
  const fromCookie = readCookie();
  if (!fromCookie) return;
  memoryToken = fromCookie.token;
  memoryExpiresAt = fromCookie.expiresAt;
  writePersisted(tokenKey(), fromCookie.token);
  writePersisted(expiresKey(), fromCookie.expiresAt);
}

function readPersisted(key: string): string {
  if (!persistEnabled()) return '';

  const fromLocal = localStorage.getItem(key) || '';
  if (fromLocal) return fromLocal;

  const fromSession = sessionStorage.getItem(key) || '';
  if (fromSession) {
    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
  }
  return fromSession;
}

function writePersisted(key: string, value: string) {
  if (!persistEnabled()) return;
  if (value) localStorage.setItem(key, value);
  else localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export const tokenStorage = {
  clear() {
    memoryToken = '';
    memoryExpiresAt = '';

    if (!persistEnabled()) return;
    localStorage.removeItem(tokenKey());
    localStorage.removeItem(expiresKey());
    sessionStorage.removeItem(tokenKey());
    sessionStorage.removeItem(expiresKey());
    writeCookie('', '');
  },
  read() {
    if (memoryToken) return memoryToken;

    memoryToken = readPersisted(tokenKey());
    if (memoryToken) return memoryToken;
    adoptCookie();
    return memoryToken;
  },
  readExpiresAt() {
    if (memoryExpiresAt) return memoryExpiresAt;

    memoryExpiresAt = readPersisted(expiresKey());
    if (memoryExpiresAt || memoryToken) return memoryExpiresAt;
    adoptCookie();
    return memoryExpiresAt;
  },
  write(token: string, expiresAt = '') {
    memoryToken = token;
    memoryExpiresAt = expiresAt;
    writePersisted(tokenKey(), token);
    writePersisted(expiresKey(), expiresAt);
    writeCookie(token, expiresAt);
  },
  /** 丢掉进程内缓存，下次从 localStorage 或 Cookie 再读。 */
  dropCache() {
    memoryToken = '';
    memoryExpiresAt = '';
  },
};
